import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FolderOpen } from "lucide-react";
import { PageShell } from "../../components/PageShell";
import { Breadcrumb } from "../../components/Breadcrumb";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Avatar, Button } from "../../components/primitives";
import { EmptyState } from "../../components/DataTable";
import { DashAntd } from "../../components/DashAntd";
import { MemberInProject } from "../../components/drilldown/MemberInProject";
import { useDrilldownData } from "../../components/drilldown/useDrilldownData";
import { DayPicture } from "../../components/drilldown/DayPicture";
import { ClassicMotion } from "../../components/classic/micro";
import {
  useClassicFilters, classicPath, personPath, personTakeawayPath, personLogPath, personProjectPath,
} from "../../components/drilldown/drilldownRoutes";
import {
  StretchAnalysis, FullWorkLog, WorkLogSkeleton, WorkLogEmpty,
  flattenStretches,
} from "../../components/drilldown/WorkLog";
import { Crumbs, ScopeBar } from "./shared";
import { useDemoMode, DemoBanner, demoWorklog } from "./demoData";
import { isGraphicalDrill } from "../graphical/drill/DrillFrame";
import { GraphicalPerson } from "../graphical/drill/GraphicalPerson";
import useEscapeBack from "../graphical/drill/useEscapeBack";

/**
 * PersonDrilldown — layers 2 and 3 of the Classic dashboard, as pages.
 *
 * This was the right-hand drawer. It is pages now because the reader asked for
 * a place they can arrive at, link to, and step back through — a drawer has no
 * address, so a refresh threw the reader back to the dashboard and a link to
 * "what Mahima did on Tuesday" could not be sent to anyone.
 *
 *   view="digest"    the graphical read: hours, the split, what they worked on
 *                    as clickable takeaways, the read, the signals
 *   view="takeaway"  ONE takeaway, opened from the digest — the detail behind
 *                    that single stretch of work
 *   view="log"       the whole written record, when the reader wants all of it
 *
 * Every panel here is a component the drawer already used, so the two cannot
 * drift; only the frame around them changed.
 */
export function PersonDrilldown({ view = "digest" }) {
  const t = useDashTheme();
  const navigate = useNavigate();
  const { userId, index, projectId } = useParams();
  const [params] = useSearchParams();
  const { range, member } = useClassicFilters();

  const nameHint = params.get("name") || "";
  const qs = params.toString();
  const link = (path) => (qs ? `${path}?${qs}` : path);

  const {
    dossier, worklog: realWorklog, worklogLoading, worklogErr,
    error,
  } = useDrilldownData({ kind: "person", id: userId, name: nameHint, range });

  const name = dossier?.identity?.name || nameHint || "Member";

  /* Sample data FILLS gaps, never replaces an answer the server gave. See
     demoData.js — off unless the URL says ?demo=1. */
  const demo = useDemoMode();
  const realHasWork = (realWorklog?.coverage?.stretches || 0) > 0;
  const worklog = demo && !worklogLoading && !realHasWork ? demoWorklog(name, range) : realWorklog;
  const stretches = React.useMemo(() => flattenStretches(worklog), [worklog]);
  const showingSample = demo && !!worklog?.__demo;
  const position = dossier?.identity?.position || "";
  const m = dossier?.metrics;

  const focusIndex = view === "takeaway" ? Number(index) : null;
  const stretch = focusIndex != null ? stretches[focusIndex] : null;

  /* view="project" keeps the PERSON as the subject and narrows to one project —
     the reverse of the project page's contributor view, which keeps the project. */
  const projectRow = projectId
    ? (dossier?.projects || []).find((p) => String(p.projectId) === String(projectId))
    : null;
  const projectName = projectRow?.name || params.get("pjn") || "Project";

  const openTakeaway = React.useCallback(
    (i) => navigate(personTakeawayPath(userId, i, range, member, name)),
    [navigate, userId, range, member, name]
  );
  const openLog = React.useCallback(
    () => navigate(personLogPath(userId, range, member, name)),
    [navigate, userId, range, member, name]
  );
  const backToDigest = React.useCallback(
    () => navigate(personPath(userId, range, member, name)),
    [navigate, userId, range, member, name]
  );

  /* Esc unwinds one level at a time — the same stepping the drawer had, now
     expressed as real navigation so Back does it too. The graphical drill uses
     the shared useEscapeBack (input-aware); the classic branch keeps its
     original listener untouched. */
  useEscapeBack(backToDigest, isGraphicalDrill() && view !== "digest");
  React.useEffect(() => {
    if (view === "digest" || isGraphicalDrill()) return undefined;
    const onKey = (e) => { if (e.key === "Escape") backToDigest(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, backToDigest]);

  const crumbs = [
    { label: "Dashboard", to: classicPath(range, member) },
    view === "digest"
      ? { label: name }
      : { label: name, to: link(personPath(userId)) },
    view === "log" ? { label: "Full work log" } : null,
    view === "takeaway"
      ? { label: stretch?.title || (Number.isFinite(focusIndex) ? `Takeaway ${focusIndex + 1}` : "Takeaway") }
      : null,
    view === "project" ? { label: projectName } : null,
  ].filter(Boolean);

  const periodLabel = m?.period?.label
    || `${new Date(range.start).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(range.end).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  const hasWork = (worklog?.coverage?.stretches || 0) > 0;

  if (isGraphicalDrill()) {
    const layer = view === "log" ? 3 : (view === "takeaway" || view === "project") ? 2 : 1;
    return (
      <GraphicalPerson
        t={t}
        view={view}
        name={name}
        position={position}
        periodLabel={periodLabel}
        userId={userId}
        range={range}
        dossier={dossier}
        m={m}
        loading={!dossier && !error}
        error={error}
        worklog={worklog}
        worklogLoading={worklogLoading}
        hasWork={hasWork}
        stretches={stretches}
        stretch={stretch}
        focusIndex={focusIndex}
        projectId={projectId}
        projectName={projectName}
        projectRow={projectRow}
        crumbs={crumbs}
        backTo={view === "digest" ? classicPath(range, member) : personPath(userId, range, member, name)}
        backLabel={layer === 1 ? "Dashboard" : "Back"}
        subject={view === "project" ? [name, projectName] : [name]}
        excludes={view === "project" ? ["every other project"] : ["everyone else on the team"]}
        openTakeaway={openTakeaway}
        openLog={openLog}
        openProject={(p) => navigate(personProjectPath(userId, p.projectId, range, member, name, p.name))}
        onBack={backToDigest}
      />
    );
  }

  return (
    <PageShell
      title={name}
      subtitle={[position, periodLabel].filter(Boolean).join(" · ")}
      icon={<Avatar user={dossier?.identity || null} name={name} size={28} />}
      actions={
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ArrowLeft size={14} />}
          onClick={() => navigate(view === "digest" ? classicPath(range, member) : personPath(userId, range, member, name))}
        >
          {view === "digest" ? "Dashboard" : "Back"}
        </Button>
      }
    >
      {/* MemberInProject (view="project") renders antd, and PageShell sits
          outside DashboardV2's provider on a routed page. */}
      <DashAntd>
      {/* Injects the shimmer/reveal keyframes for this routed page — without it
          every <Shim/> in the drill-down (L1/L2/L3, work log) renders as a
          static box instead of a moving skeleton. */}
      <ClassicMotion />
      <Crumbs><Breadcrumb items={crumbs} /></Crumbs>
      {showingSample && <DemoBanner t={t} what="Some of what is shown" />}

      {error ? (
        <EmptyState icon={<FolderOpen size={22} />} title="Couldn’t load this drill-down" description={error} />
      ) : view === "takeaway" ? (
        /* ── LAYER 3 — one takeaway, in full ─────────────────────────────── */
        worklogLoading ? (
          <WorkLogSkeleton />
        ) : worklogErr ? (
          <EmptyState icon={<FolderOpen size={22} />} title="Couldn’t load the work log" description={worklogErr} />
        ) : stretch ? (
          <StretchAnalysis
            userId={userId}
            stretch={stretch}
            index={focusIndex}
            total={stretches.length}
            timeline={dossier?.timeline}
            onBack={backToDigest}
            onJump={openTakeaway}
            onFullLog={openLog}
          />
        ) : (
          <EmptyState
            icon={<FolderOpen size={22} />}
            title="That takeaway isn’t in this window"
            description="The date range changed, so this stretch is no longer in the record. Open the full work log to see what is."
          />
        )
      ) : view === "project" ? (
        /* ── this person, narrowed to one project ────────────────────────── */
        <MemberInProject
          member={{
            userId,
            firstName: name,
            lastName: "",
            totalHours: (projectRow?.trackedMinutes || 0) / 60,
          }}
          project={{ id: projectId, name: projectName }}
          range={range}
          onBack={backToDigest}
          /* No hop offered: we are already inside this person. */
          onOpenPerson={null}
        />
      ) : view === "log" ? (
        /* ── the whole written record ────────────────────────────────────── */
        worklogLoading ? (
          <WorkLogSkeleton />
        ) : worklogErr ? (
          <EmptyState icon={<FolderOpen size={22} />} title="Couldn’t load the work log" description={worklogErr} />
        ) : hasWork ? (
          <FullWorkLog worklog={worklog} onPick={openTakeaway} />
        ) : (
          <WorkLogEmpty t={t} worklog={worklog} name={name} />
        )
      ) : (
        /* ── LAYER 2 — the AI picture is the whole view now ─────────────── */
        <div style={{ display: "flex", flexDirection: "column", gap: t.s[4], minWidth: 0 }}>
          <ScopeBar
            t={t}
            subject={[name]}
            excludes={["everyone else on the team"]}
          />
          <DayPicture userId={userId} range={range} name={name} />
        </div>
      )}
      </DashAntd>
    </PageShell>
  );
}

export default PersonDrilldown;
